import { Component } from '@theme/component';

class OfficeOrbit extends Component {
  #controller;
  #observer;
  #resize;
  #motion;
  #animations = [];
  #burst;
  #visible = false;
  #paused = false;
  #interacting = false;
  #width = 0;
  #duration = 34000;

  connectedCallback() {
    super.connectedCallback();
    this.#dispose();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motion.addEventListener('change', this.#sync, { signal });
    document.addEventListener('visibilitychange', this.#sync, { signal });
    window.addEventListener('pageshow', this.#reset, { signal });
    this.addEventListener('pointerover', this.#enter, { signal });
    this.addEventListener('pointerout', this.#leave, { signal });
    this.addEventListener('focusin', this.#enter, { signal });
    this.addEventListener('focusout', this.#leave, { signal });
    this.addEventListener('pointerdown', this.#activate, { signal });
    this.addEventListener('keydown', this.#activate, { signal });
    this.#observer = new IntersectionObserver(entries => {
      this.#visible = entries.some(entry => entry.isIntersecting);
      this.#sync();
    }, { threshold: 0 });
    this.#observer.observe(this);
    this.#resize = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (width && Math.abs(width - this.#width) > 1) this.#build(width);
    });
    if (this.refs.scene) this.#resize.observe(this.refs.scene);
    this.dataset.ready = '';
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#resize?.disconnect();
    if (this.refs.scene) {
      this.#resize?.observe(this.refs.scene);
      this.#build(this.refs.scene.getBoundingClientRect().width);
    }
    this.#sync();
  }

  disconnectedCallback() {
    this.#dispose();
    delete this.dataset.ready;
    super.disconnectedCallback();
  }

  toggleMotion() {
    this.#paused = !this.#paused;
    this.#sync();
  }

  #dispose() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#resize?.disconnect();
    for (const animation of this.#animations) animation.cancel();
    this.#animations = [];
    this.#burst?.cancel();
    this.#width = 0;
  }

  #build(width) {
    const progress = Number(this.#animations[0]?.currentTime ?? 0) / this.#duration;
    for (const animation of this.#animations) animation.cancel();
    this.#animations = [];
    this.#width = width;
    this.#duration = width < 400 ? 40000 : 34000;
    for (const planet of this.querySelectorAll('[data-orbit-index]')) {
      const travel = planet.querySelector('.ol-hero__travel');
      if (!travel) continue;
      const phase = planet.dataset.orbitIndex === '2' ? Math.PI : 0;
      const frames = Array.from({ length: 121 }, (_, i) => {
        const angle = i / 120 * Math.PI * 2 + phase;
        const x = Math.cos(angle) * width * .35;
        const y = Math.sin(angle) * width * .14;
        const scale = .88 + Math.sin(angle) * .12;
        return { transform: `translate(-50%,-50%) translate3d(${x.toFixed(3)}px,${y.toFixed(3)}px,0) scale(${scale.toFixed(4)})` };
      });
      const path = travel.animate(frames, { duration:this.#duration, iterations:Infinity, easing:'linear', fill:'both' });
      const front = phase === 0 ? 4 : 2;
      const back = phase === 0 ? 2 : 4;
      const depth = planet.animate([{zIndex:front,offset:0},{zIndex:back,offset:.5},{zIndex:front,offset:1}], {duration:this.#duration,iterations:Infinity,easing:'steps(1,end)',fill:'both'});
      path.pause(); depth.pause();
      path.currentTime = progress * this.#duration;
      depth.currentTime = progress * this.#duration;
      this.#animations.push(path,depth);
    }
    this.#sync();
  }

  #select(link) {
    if (!link?.dataset.orbitProduct) return;
    if (this.refs.edition) this.refs.edition.href = link.href;
    if (this.refs.editionTitle) this.refs.editionTitle.textContent = link.dataset.orbitTitle;
    if (this.refs.editionPrice) this.refs.editionPrice.textContent = link.dataset.orbitPrice;
    for (const entry of this.querySelectorAll('.ol-hero__product-rail a')) {
      entry.dataset.orbitSelected = String(entry.dataset.orbitProduct === link.dataset.orbitProduct);
    }
  }

  #enter = event => {
    const link = event.target instanceof Element ? event.target.closest('[data-orbit-product]') : null;
    if (!link || !this.contains(link)) return;
    if (event.type === 'pointerover' && event.pointerType === 'touch') return;
    this.#select(link);
    this.#interacting = true;
    this.#sync();
  };

  #leave = event => {
    const next = event.relatedTarget instanceof Element ? event.relatedTarget.closest('[data-orbit-product]') : null;
    if (next && this.contains(next)) return;
    this.#interacting = false;
    this.#sync();
  };

  #activate = event => {
    if (event.type === 'keydown' && event.key !== 'Enter') return;
    const link = event.target instanceof Element ? event.target.closest('[data-orbit-product],.ol-hero__edition') : null;
    if (!link) return;
    this.#select(link);
    if (this.#motion?.matches || !this.refs.flare) return;
    this.#burst?.cancel();
    this.#burst = this.refs.flare.animate([{opacity:.65,transform:'scale(.75)'},{opacity:0,transform:'scale(1.9)'}],{duration:420,easing:'cubic-bezier(.16,1,.3,1)'});
  };

  #reset = () => { this.#interacting = false; this.#sync(); };

  #sync = () => {
    const reduced = this.#motion?.matches ?? true;
    const running = !this.#paused && !reduced && this.#visible && !document.hidden && !this.#interacting;
    this.dataset.paused = String(this.#paused);
    this.dataset.running = String(running);
    for (const animation of this.#animations) {
      if (running && animation.playState !== 'running') animation.play();
      else if (!running && animation.playState !== 'paused') animation.pause();
      if (reduced) animation.currentTime = 0;
    }
    const button = this.refs.toggle;
    if (!button) return;
    button.hidden = reduced;
    const label = this.#paused ? button.dataset.play : button.dataset.pause;
    if (label) { button.setAttribute('aria-label',label); button.title = label; }
  };
}

if (!customElements.get('ol-orbit-component')) customElements.define('ol-orbit-component',OfficeOrbit);
